// Shared formatting helpers used across the UI.

export function formatBytes(n: number | null | undefined): string {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${units[i]}`;
}

export function formatDuration(ms: number | null | undefined): string {
  if (ms == null) return "—";
  const totalSec = ms / 1000;
  if (totalSec < 60) return `${totalSec.toFixed(1)}s`;
  const m = Math.floor(totalSec / 60);
  const s = Math.round(totalSec % 60);
  return `${m}m ${String(s).padStart(2, "0")}s`;
}

/** Aggregate durations at hours scale ("12h 34m" / "45m") — formatDuration is per-video. */
export function formatHours(ms: number | null | undefined): string {
  if (ms == null) return "—";
  const totalMin = Math.round(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h === 0) return `${m}m`;
  return `${h}h ${String(m).padStart(2, "0")}m`;
}

/** Chart-axis tick for millisecond values on an hours scale. */
export function hoursTick(ms: number): string {
  const h = ms / 3_600_000;
  if (h === 0) return "0";
  return h >= 10 ? `${Math.round(h)}h` : `${h.toFixed(1)}h`;
}

export function formatNumber(v: number | null | undefined, digits = 2): string {
  if (v == null) return "—";
  if (Number.isInteger(v)) return String(v);
  return v.toFixed(digits);
}

export function formatPct(rate: number | null | undefined, digits = 0): string {
  if (rate == null) return "—";
  return `${(rate * 100).toFixed(digits)}%`;
}

/** Compact relative-ish time string + absolute on hover. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** When the recording was captured — parsed from the log id stamp (YYYYMMDD-HHMMSS-…).
 * Falls back to `fallbackIso` (row creation time) for ids without a parsable stamp; note
 * that for imported legacy recordings created_at is the import date, not the capture date. */
export function recordingDate(logId: string, fallbackIso: string | null | undefined): string {
  const m = logId.match(/^(\d{4})(\d{2})(\d{2})-(\d{2})(\d{2})(\d{2})/);
  const d = m
    ? new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6])
    : fallbackIso
      ? new Date(fallbackIso)
      : null;
  if (!d || Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function timeAgo(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso).getTime();
  if (Number.isNaN(d)) return "—";
  const diff = Date.now() - d;
  const sec = Math.round(diff / 1000);
  if (sec < 60) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  return `${day}d ago`;
}
